/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import React, { ReactNode, SyntheticEvent, useEffect } from "react";

import { useTranslation } from "react-i18next";

import useCompaniesCheckboxFuntions from "@/components/Table/hooks/useCompaniesCheckboxFunctions";
import { useTableStoreByRoute } from "@/stores/useTableStore";
import TableCell from "@mui/material/TableCell";

import { colors } from "@dotnet/telematics-design-system";

import { ROUTES } from "../../../../../shared";
import { removeSlashFromPath } from "../../../../../shared/functions/functions";
import Checkbox from "../../../../CheckBox/Checkbox";
import useTutorialMode from "../../../../TutorialMode/hooks/useTutorialMode";
import { STEP_GROUP } from "../../../../TutorialMode/types";
import { useTableStyles } from "../../../styles/styles";

interface ITableCheckboxProps {
  route: ROUTES;
  fromTableRow?: boolean;
  isRowChecked?: any;
  row?: any;
  onTableHeadCheckboxChange?: any;
  isChecked?: boolean;
  fromMobileTableItem?: boolean;
  disabled?: boolean;
  index?: number;
  companyLevelsList?: number[];
}

const TableCheckbox = ({
  route,
  fromTableRow,
  isRowChecked,
  row,
  onTableHeadCheckboxChange,
  isChecked,
  fromMobileTableItem,
  disabled,
  index,
  companyLevelsList
}: ITableCheckboxProps) => {
  const { t } = useTranslation();

  const rowId = row?.id;

  const checkedItem = useTableStoreByRoute(route, state =>
    state.selectedRows.includes(rowId)
  );

  const selectRow = useTableStoreByRoute(route, state => state.selectRow);

  const deselectRow = useTableStoreByRoute(route, state => state.deselectRow);

  const {
    generateEmptyCellCountOfIndexNumber,
    generateIndentedCheckboxDivWithCompanyName,
    mapThroughCompaniesLevels
  } = useCompaniesCheckboxFuntions(
    row,
    companyLevelsList as number[],
    checkedItem,
    index
  );

  const {
    stepIndex,
    stepGroup,
    checkIfCorrectTutorialStep,
    setTutorialDetails
  } = useTutorialMode();

  const { tableCheckboxCell, headCheckboxInnerStyle, checkboxCellStyle } =
    useTableStyles();

  useEffect(() => {
    if (isRowChecked) {
      selectRow(rowId);
    }
  }, [isRowChecked, rowId, selectRow]);

  const handleRowCheckboxChange = () => {
    if (!fromTableRow) return;

    if (checkedItem) {
      deselectRow(rowId);
    } else {
      selectRow(rowId);
    }
  };

  const companiesPage = route === ROUTES.Companies;

  const arrayOfRowLevels = [...new Array(row?.level).fill(row?.level)];

  const boxShadowBottom = `inset 0px -1px 0px ${colors.gray200}`;
  const checkedLeftBorder = `inset 2px 0px 0px ${colors.blue700}`;
  const leftBorderWithWarning = `inset 2px 0px 0px ${colors.orange600}`;

  const checkedItemsBxShadow =
    checkedItem && `${checkedLeftBorder}, ${boxShadowBottom}`;

  const noSchedulesBoxShadow =
    row?.schedulesList?.length === 0 &&
    ` ${leftBorderWithWarning}, ${boxShadowBottom}`;

  const renderOptionalTableCell = (children: ReactNode) => {
    if (fromMobileTableItem) {
      return children;
    }

    if (companiesPage) {
      return (
        <TableCell
          css={css({
            padding:
              row?.level === 0 ? "0 0 0 16px !important" : "0 !important",
            display: "flex",
            boxShadow: boxShadowBottom
          })}
        >
          {companyLevelsList &&
            arrayOfRowLevels.map((_level, levelIndex) => {
              if (levelIndex !== 0) {
                // generate empty cells in front of checkbox to make space for hierarchy lines drawing.
                // empty cells count depends on company level number
                return generateEmptyCellCountOfIndexNumber(
                  Boolean(
                    mapThroughCompaniesLevels(levelIndex, arrayOfRowLevels)
                  ),
                  levelIndex,
                  arrayOfRowLevels
                );
              }
              return generateEmptyCellCountOfIndexNumber(
                false,
                levelIndex,
                arrayOfRowLevels
              );
            })}

          {/* display Companies page checkbox together with company name for exact design implementation */}
          {generateIndentedCheckboxDivWithCompanyName(children)}
        </TableCell>
      );
    }

    return (
      <TableCell
        css={css([
          {
            boxShadow:
              checkedItemsBxShadow || noSchedulesBoxShadow || boxShadowBottom,

            zIndex: "3",
            alignItems: "center"
          },
          tableCheckboxCell
        ])}
      >
        <div css={css({ height: "24px" })}>{children}</div>
      </TableCell>
    );
  };

  const handleCheckboxClick = (event: SyntheticEvent) => {
    event.stopPropagation();

    if (
      (fromMobileTableItem &&
        checkIfCorrectTutorialStep(STEP_GROUP.TutorialApplySchedule, 3)) ||
      checkIfCorrectTutorialStep(STEP_GROUP.TutorialApplySchedule, 1)
    ) {
      setTutorialDetails({
        isTutorialOpen: true,
        stepIndex: stepIndex + 1,
        stepGroup: stepGroup,
        isTutorialActive: true
      });
    }
  };

  return (
    <>
      {fromTableRow ? (
        renderOptionalTableCell(
          <Checkbox
            id={`checkbox_${index}_${removeSlashFromPath(route)}_body`}
            checked={checkedItem}
            disabled={disabled}
            onChange={handleRowCheckboxChange}
            onClick={handleCheckboxClick}
          />
        )
      ) : (
        // Table head checkbox
        <TableCell css={css(checkboxCellStyle)}>
          <div css={css(headCheckboxInnerStyle)}>
            <Checkbox
              id={`table_head_${row?.id}`}
              checked={isChecked}
              onChange={onTableHeadCheckboxChange}
              disabled={disabled}
            />

            {/* Display Table header checkbox together with Companies page column name for exact design implementation */}
            {companiesPage && (
              <div css={css({ paddingLeft: "12px" })}>
                {t(`Table##company`)}
              </div>
            )}
          </div>
        </TableCell>
      )}
    </>
  );
};

export default React.memo(TableCheckbox);
